import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@foodpilot/foods";
import { Box, CircularProgress, Stack } from "@mui/material";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";

import { CMSLayout } from "../CMS/CMSLayout";
import {
    useAllExtensions,
    useCreatePuckExtension,
    useUpdatePuckExtension,
    useDeletePuckExtension,
    type CreatePuckExtensionRequest,
} from "src/components/PuckExtensions";
import { PuckExtension } from "src/api/client-api/puckExtensions/useExtensions";
import { useGetAllSectors } from "src/api/client-api/companySectors/useSupplierSectors";
import { DynaBlockTable } from "./components/DynaBlockTable";
import { CreateDynaBlockDialog } from "./components/CreateDynaBlockDialog";
import { DeleteConfirmDialog } from "./components/DeleteConfirmDialog";
import { DynaBlockSettingsDialog } from "./components/DynaBlockSettingsDialog";
import { CREATE, DELETE, MODIFY, PUCK_EXTENSION, useFoodpilotContext } from "src/context/FoodpilotContext";

export const DynaBlockManager = () => {
    const { t } = useTranslation();
    const { canUser } = useFoodpilotContext();
    const canCreate = canUser(CREATE, PUCK_EXTENSION);
    const canModify = canUser(MODIFY, PUCK_EXTENSION);
    const canDelete = canUser(DELETE, PUCK_EXTENSION);

    const { companySectors: allSectors } = useGetAllSectors({ itemsPerPage: 200 });

    // Mutations
    const createExtensionMutation = useCreatePuckExtension();
    const updateExtensionMutation = useUpdatePuckExtension();
    const deleteExtensionMutation = useDeletePuckExtension();

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [extensionToDelete, setExtensionToDelete] = useState<number | null>(null);
    const [settingsDialogExtension, setSettingsDialogExtension] = useState<PuckExtension | null>(null);

    const PAGE_SIZE = 20;
    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "createdAt", desc: true }]);
    const [columnFilters, setColumnFilters] = useState<{ id: string; value: any }[]>([]);

    const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value || "";
    const statusFilter = columnFilters.find((filter) => filter.id === "status")?.value || "";
    const extensionPointFilter = columnFilters.find((filter) => filter.id === "extensionPoint")?.value || "";

    const orderBy: { createdAt?: "asc" | "desc"; updatedAt?: "asc" | "desc"; name?: "asc" | "desc" } = {};
    sorting.forEach((sort) => {
        if (sort.id === "createdAt") orderBy.createdAt = sort.desc ? "desc" : "asc";
        if (sort.id === "updatedAt") orderBy.updatedAt = sort.desc ? "desc" : "asc";
        if (sort.id === "name") orderBy.name = sort.desc ? "desc" : "asc";
    });

    const {
        extensions = [],
        totalItems,
        isLoading,
        error,
        refetch,
    } = useAllExtensions({
        search: nameFilter || undefined,
        status: statusFilter || undefined,
        extensionPoint: extensionPointFilter || undefined,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
    });

    const isError = !!error;

    // const navigateToEditor = (extensionId: number) => {
    //     navigate(`/settings/cms/dynablocks/${extensionId}`);
    // };

    const confirmDelete = (extensionId: number) => {
        setExtensionToDelete(extensionId);
        setDeleteConfirmDialogOpen(true);
    };

    const handleDeleteExtension = async () => {
        if (!extensionToDelete) return;

        deleteExtensionMutation.mutate(extensionToDelete, {
            onSuccess: () => {
                setDeleteConfirmDialogOpen(false);
                setExtensionToDelete(null);
            },
            onError: (error) => {
                console.error("Error deleting extension:", error);
                // TODO: Show user-friendly error message
            },
        });
    };

    const handleCreateExtension = async (extensionData: CreatePuckExtensionRequest) => {
        createExtensionMutation.mutate(extensionData, {
            onSuccess: () => {
                setIsCreateDialogOpen(false);
            },
            onError: (error) => {
                console.error("Error creating extension:", error);
                // TODO: Show user-friendly error message
            },
        });
    };

    const handleUpdateExtensionName = async (extensionId: number, newName: string) => {
        updateExtensionMutation.mutate(
            {
                id: extensionId,
                name: newName,
            },
            {
                onError: (error) => {
                    console.error("Error updating extension name:", error);
                    // TODO: Show user-friendly error message
                },
            },
        );
    };

    const handleUpdateExtensionSettings = async (extensionId: number, allowedRoles: string[], sectors: string[]) => {
        updateExtensionMutation.mutate(
            { id: extensionId, allowedRoles, sectors },
            {
                onSuccess: () => setSettingsDialogExtension(null),
                onError: (error) => {
                    console.error("Error updating extension settings:", error);
                },
            },
        );
    };

    const handleUpdateExtensionStatus = async (extensionId: number, newStatus: string) => {
        updateExtensionMutation.mutate(
            {
                id: extensionId,
                status: newStatus as "active" | "inactive" | "draft",
            },
            {
                onError: (error) => {
                    console.error("Error updating extension status:", error);
                    // TODO: Show user-friendly error message
                },
            },
        );
    };

    const topBar =
        canCreate ?
            <Stack direction="row" spacing={2} alignItems="center">
                <Button
                    variant="contained"
                    onClick={() => setIsCreateDialogOpen(true)}
                    disabled={isLoading || createExtensionMutation.isLoading}
                    sx={(theme) => ({
                        borderRadius: "44px",
                        padding: "0px 20px",
                        height: "40px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        textTransform: "none",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                            color: "white !important",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            opacity: 0.36,
                            color: "white !important",
                        },
                    })}
                >
                    {t("settings.cms.dynablocks.create_dynablock", "Create DynaBlock")}
                </Button>

                {(isLoading || createExtensionMutation.isLoading) && (
                    <Box display="flex" alignItems="center">
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        {t("common.loading", "Loading...")}
                    </Box>
                )}
            </Stack>
        :   undefined;

    return (
        <CMSLayout
            title={t("settings.cms.dynablocks.title", "DynaBlock Manager")}
            isLoading={isLoading}
            isError={isError}
            topBar={topBar}
        >
            <DynaBlockTable
                extensions={extensions}
                allSectors={allSectors}
                onDelete={confirmDelete}
                onUpdateName={handleUpdateExtensionName}
                onUpdateStatus={handleUpdateExtensionStatus}
                onOpenSettings={setSettingsDialogExtension}
                pagination={pagination}
                setPagination={setPagination}
                sorting={sorting}
                setSorting={setSorting}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                totalItems={totalItems}
                isServerSide={true}
            />

            {canCreate && (
                <CreateDynaBlockDialog
                    open={isCreateDialogOpen}
                    onClose={() => setIsCreateDialogOpen(false)}
                    onCreateDynaBlock={handleCreateExtension}
                    isCreating={createExtensionMutation.isLoading}
                />
            )}

            {canDelete && (
                <DeleteConfirmDialog
                    open={deleteConfirmDialogOpen}
                    onClose={() => setDeleteConfirmDialogOpen(false)}
                    onConfirmDelete={handleDeleteExtension}
                    isDeleting={deleteExtensionMutation.isLoading}
                    extensionName={extensions.find((ext) => ext.id === extensionToDelete)?.name || ""}
                />
            )}

            {canModify && (
                <DynaBlockSettingsDialog
                    open={settingsDialogExtension !== null}
                    extension={settingsDialogExtension}
                    onClose={() => setSettingsDialogExtension(null)}
                    onSave={handleUpdateExtensionSettings}
                    isSaving={updateExtensionMutation.isLoading}
                />
            )}
        </CMSLayout>
    );
};
